import { useUpdateTreatmentHistoriesSortOrder } from "../hooks/useTreatmentHistory";
import DatePicker from '@oncolens/oncolens-ui/lib/components/FormControls/DatePicker';
import Listbox from '@oncolens/oncolens-ui/lib/components/FormControls/Listbox';
import Checkbox from '@oncolens/oncolens-ui/lib/components/FormControls/Checkbox';
import TextAreaInput from '@oncolens/oncolens-ui/lib/components/FormControls/TextAreaInput';
import Typeahead from "@oncolens/oncolens-ui/lib/components/FormControls/Typeahead/Typeahead";
import { Controller, ControllerRenderProps, FieldValues, UseFormWatch } from 'react-hook-form';
import shallow from 'zustand/shallow';
import { useDefinitionConfig } from '../hooks/useCancerConfig';
import { treatmentHistoryKeys } from '../TreatmentHistory/treatmentHistory.keys';
import { createTreatmentHistory, getTreatmentHistory, useCreateTreatmentHistoryItem, useDeleteTreatmentHistoryItem, useUpdateTreatmentHistoryItem } from '../hooks/useTreatmentHistory';
import { useGetTreatmentMappings } from "../hooks/useTreatmentMapping";
import useRequestFormStore from '../store';
import { usePostCaseConfigs } from '../hooks/PostCaseUtilities';
import { treatmentHistorySchema } from '../TreatmentHistory/schema';
import { useQueryParameters } from "@/hooks/useQueryParameters";
import MultiItemDynamicForm from "@/components/Elements/Form/MultiDynamicForm";
import { useEffect } from "react";

interface TreatmentDescriptionSuggestionsProps {
  watch: UseFormWatch<FieldValues>;
  field: ControllerRenderProps<FieldValues, "treatmentDescription">;
  className?: string;
};

const TreatmentDescriptionSuggestions = ({ watch, field, className }: TreatmentDescriptionSuggestionsProps) => {
  const [
    cancerPathologyIds
  ] = useRequestFormStore(store => {
    const diseases = store.getValue('diseases');
    return [
      diseases ? Object.values(diseases)
        .filter((disease: any) => disease.cancerPathologyId)
        .map((disease: any) => disease.cancerPathologyId)
        : []
    ];
  }, shallow);

  const { data: treatmentHistoryData } = useDefinitionConfig("Treatment History");

  const treatmentType = watch('treatmentType');
  const treatmentTypeItem = (treatmentHistoryData ?? []).find((item) => item.label === treatmentType);

  const { data: treatmentMappingsData } = useGetTreatmentMappings(
    cancerPathologyIds,
    treatmentTypeItem && treatmentTypeItem.value ? treatmentTypeItem.value : '',
  );

  const mappedTreatmentData = treatmentMappingsData?.map(
    (treatmentMapping) => ({
      label: treatmentMapping.treatmentDescription,
      value: treatmentMapping.treatmentDescription,
    })
  ) || [];

  return (
    <Typeahead
      id="treatment-typeahead"
      data={mappedTreatmentData}
      label="Treatment"
      onChange={v => {
        field.onChange(v?.label);
      }}
      selected={field.value ? { label: field.value || '', value: field.value } : null}
      className={className}
      options={{ allowCustomInput: true }}
      tooltipOptions={{ text: 'Generic drug names are preferred for best type ahead search results' }}
      sortingOptions={{ sortable: true, sortProperty: 'label' }}
    />
  );
};

const TreatmentHistoryForm = () => {
  const queryParameters = useQueryParameters();
  const patientId = queryParameters.get('patientId') ?? '';
  const eventId = queryParameters.get('eventId') ?? '';
  const createMutation = useCreateTreatmentHistoryItem();
  const updateMutation = useUpdateTreatmentHistoryItem();
  const updateSortListMutation = useUpdateTreatmentHistoriesSortOrder();
  const deleteMutation = useDeleteTreatmentHistoryItem();
  const [
    updateField,
    histories,
    noTreatmentHistory,
  ] = useRequestFormStore(store => [
    store.updateField,
    store.treatmentHistory,
    store.noTreatmentHistory
  ], shallow);

  const { data: treatmentHistoryData } = useDefinitionConfig("Treatment History");
  const treatmentHistoryDataSelectItems = treatmentHistoryData?.map((item) => ({
    label: item.label,
    value: item.label
  }));
  const configs = usePostCaseConfigs(['treatment_history_status']);

  const options = {
    formOptions: {
      setValue: updateField, items: histories,
    },
    hydrationOptions: {
      getItemsForHydration: async () => getTreatmentHistory(patientId),
      formKey: 'treatmentHistory',
      cacheKey: treatmentHistoryKeys.list(patientId),
    },
    shouldUseApiActionOnChange: Boolean(patientId) && Boolean(eventId),
    idForSave: patientId,
    addItemLabel: 'Add treatment history...',
    itemNameForFormTitle: 'Treatment History',
    schema: treatmentHistorySchema,
    createMutation,
    updateSortListMutation,
    updateMutation,
    deleteMutation,
    isSortable: true,
    testSelector: 'section-treatment-history',
  };

  useEffect(() => {
    if (histories?.length > 0 && noTreatmentHistory) {
      updateField('noTreatmentHistory', false);
    }
  }, [histories, noTreatmentHistory, updateField]);

  return (
    <>
      <Checkbox
        id="noTreatmentHistory"
        htmlName="noTreatmentHistory"
        label="No Treatment History"
        value={noTreatmentHistory}
        disabled={histories.length > 0}
        onClick={() => updateField('noTreatmentHistory', !noTreatmentHistory)}
        required={!histories.length}
      />
      <MultiItemDynamicForm
        {...options}
        deleteModalHeader={"Delete Treatment History"}
        deleteModalWarning={"Are you sure you want to delete this treatment history?"}
        displayTemplate={
          data => (
            <div className="flex flex-wrap sm:flex-nowrap">
              <div className="w-1/2 mb-2 sm:mb-0 sm:pr-4 sm:w-auto">
                <label className="text-sm font-semibold text-gray-800 whitespace-nowrap">Treatment Type</label>
                <p className="text-sm" data-cy="item-display-treatment-type">{data.treatmentType}</p>
              </div>
              <div className="w-1/2 mb-2 sm:mb-0 sm:px-4 sm:w-auto">
                <label className="text-sm font-semibold text-gray-800">Treatment</label>
                <p className="text-sm" data-cy="item-display-treatment-description">{data.treatmentDescription || '—'}</p>
              </div>
              <div className="w-1/2 mb-2 sm:mb-0 sm:px-4 sm:w-auto">
                <label className="text-sm font-semibold text-gray-800">Start Date</label>
                <p className="text-sm" data-cy="item-display-start-date">{data?.startDate?.toLocaleDateString() || '—'}</p>
              </div>
              <div className="w-1/2 mb-2 sm:mb-0 sm:px-4 sm:w-auto">
                <label className="text-sm font-semibold text-gray-800">End Date</label>
                <p className="text-sm" data-cy="item-display-end-date">{data?.endDate?.toLocaleDateString() || '—'}</p>
              </div>
              <div className="w-1/2 mb-2 sm:mb-0 sm:px-4 sm:w-auto">
                <label className="text-sm font-semibold text-gray-800">Status</label>
                <p className="text-sm" data-cy="item-display-status">{data.status || '—'}</p>
              </div>
              <div className="w-1/2 mb-2 sm:mb-0 sm:px-4 sm:w-auto">
                <label className="text-sm font-semibold text-gray-800">Other details</label>
                <p className="text-sm" data-cy="item-display-comments">{data.comments || '—'}</p>
              </div>
            </div>
          )
        }
        formTemplate={
          (control, register, errors, watch) => (
            <>
              <Controller
                name="treatmentType"
                control={control}
                render={
                  ({ field }) => (
                    <Listbox
                      label="Treatment Type"
                      data={treatmentHistoryDataSelectItems ?? []}
                      selected={field.value}
                      setSelected={field.onChange}
                      required={true}
                      error={errors?.treatmentType?.message}
                      wrapperProps={{ className: 'col-span-5 sm:col-span-2' }}
                    />
                  )
                }
              />

              <Controller
                name='treatmentDescription'
                control={control}
                render={({ field }) => (
                  <TreatmentDescriptionSuggestions
                    watch={watch}
                    field={field}
                    className='col-span-5 sm:col-span-3'
                  />
                )} />

              <Controller
                name="startDate"
                control={control}
                render={
                  ({ field }) => (
                    <DatePicker
                      id="startDate"
                      htmlName="startDate"
                      label="Start Date"
                      selected={field.value}
                      setSelected={field.onChange}
                      wrapperProps={{ className: 'col-span-5 sm:col-span-1' }}
                    />
                  )
                }
              />

              <Controller
                name="endDate"
                control={control}
                render={
                  ({ field }) => (
                    <DatePicker
                      id="endDate"
                      htmlName="endDate"
                      label="End Date"
                      selected={field.value}
                      setSelected={field.onChange}
                      wrapperProps={{ className: 'col-span-5 sm:col-span-1' }}
                    />
                  )
                }
              />

              <Controller
                name="status"
                control={control}
                render={
                  ({ field }) => (
                    <Listbox
                      label="Status"
                      data={configs?.treatment_history_status ?? []}
                      selected={field.value}
                      setSelected={field.onChange}
                      required
                      error={errors?.status?.message}
                      wrapperProps={{ className: 'col-span-5 sm:col-span-2' }}
                    />
                  )
                }
              />

              <Controller
                name='comments'
                control={control}
                render={({ field }) => (
                  <TextAreaInput
                    id="comments"
                    label="Other details"
                    htmlName="comments"
                    value={field.value}
                    onChange={field.onChange}
                    wrapperProps={{ className: 'col-span-5 sm:col-span-5' }}
                  />
                )} />
            </>
          )
        }
      />
    </>
  );
};

TreatmentHistoryForm.save = async (patientId: string) => {
  const { treatmentHistory, updateField } = useRequestFormStore.getState();

  return createTreatmentHistory(patientId, treatmentHistory)
    .then(result => {
      updateField('treatmentHistory', result);
    })
}

export { TreatmentHistoryForm as default };

